<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="imeiInformationModalTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="imeiInformationModalTitle">IMEI Details</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Schließen"
          ></button>
        </div>

        <div class="modal-body">
          <div v-if="state === 'loading' || state === 'fetching'" class="info-status">
            <div class="spinner" aria-label="Lädt"></div>
            <span>{{ state === 'fetching' ? 'Die Schnittstelle wird abgefragt…' : 'Auskunft wird geladen…' }}</span>
          </div>

          <!-- Nichts gespeichert: die Abfrage kostet Guthaben, deshalb erst auf
               ausdrücklichen Klick und nicht schon beim Öffnen. -->
          <div v-else-if="state === 'not_stored'" class="info-empty">
            <p>Zu dieser IMEI/SN liegt noch keine Detailauskunft vor.</p>
            <p class="info-hint">Die Abfrage bei der Schnittstelle ist kostenpflichtig.</p>
            <button type="button" class="btn btn-primary" @click="onFetch">Jetzt abfragen</button>
          </div>

          <div v-else-if="state === 'error'" class="error">{{ errorMessage }}</div>

          <template v-else>
            <p class="info-meta">
              Abfrage vom {{ formatDate(checkedAt) }} · Service {{ serviceId }}
            </p>
            <!-- Fremdes HTML vom Schnittstellenanbieter: abgeschottet im iframe,
                 ohne Skripte und ohne Zugriff auf diese Seite. -->
            <iframe
              class="info-frame"
              sandbox=""
              referrerpolicy="no-referrer"
              title="Antwort der IMEI-Schnittstelle"
              :srcdoc="documentHtml"
            ></iframe>
          </template>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Schließen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import { fetchImeiInformation, getImeiInformation, type ImeiInformation } from '../services/tickets';

/**
 * Zeigt die HTML-Detailauskunft der IMEI-Schnittstelle zu einem Ticket ODER einem
 * Lagergerät. Aufruf via `open(ticketId)` (Ticket) oder `open({ get, fetch })` mit
 * zwei Ladern (jede andere Quelle, heute das Lagergerät). Gespeichertes wird beim
 * Öffnen geladen; liegt nichts vor, bietet das Modal die kostenpflichtige Abfrage
 * an — die läuft nie automatisch.
 *
 * Das Promise löst beim Schließen auf und sagt, ob nun eine Auskunft vorliegt —
 * damit die aufrufende Ansicht ihre Knopfbeschriftung nachziehen kann.
 */
export interface ImeiInformationSource {
  get:   () => Promise<ImeiInformation>;
  fetch: () => Promise<ImeiInformation>;
}
const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;

type State = 'loading' | 'shown' | 'not_stored' | 'fetching' | 'error';

const state        = ref<State>('loading');
const errorMessage = ref('');
const documentHtml = ref('');
const checkedAt    = ref<string | null>(null);
const serviceId    = ref<number | null>(null);

let source: ImeiInformationSource | null = null;
let hasInformation = false;
let resolver: ((hasInformation: boolean) => void) | null = null;

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    // Deckt alle Schließwege ab (X, Schließen, Backdrop, ESC).
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

function open(idOrSource: number | ImeiInformationSource): Promise<boolean> {
  source = typeof idOrSource === 'number'
    ? { get: () => getImeiInformation(idOrSource), fetch: () => fetchImeiInformation(idOrSource) }
    : idOrSource;
  hasInformation = false;
  state.value        = 'loading';
  errorMessage.value = '';
  documentHtml.value = '';
  checkedAt.value    = null;
  serviceId.value    = null;
  modal?.show();
  void load();
  return new Promise<boolean>(resolve => {
    resolver = resolve;
  });
}

async function load(): Promise<void> {
  if (source === null) return;
  try {
    show(await source.get());
  } catch (e) {
    // 404 heißt hier nicht „Fehler", sondern „noch nichts gespeichert". Beide
    // Services (Ticket, Lagergerät) haben ihre eigene ApiError-Klasse, geprüft
    // wird deshalb nur das Feld.
    if (typeof e === 'object' && e !== null && (e as { status?: number }).status === 404) {
      state.value = 'not_stored';
      return;
    }
    fail(e);
  }
}

async function onFetch(): Promise<void> {
  if (source === null) return;
  state.value = 'fetching';
  try {
    show(await source.fetch());
  } catch (e) {
    fail(e);
  }
}

function show(information: ImeiInformation): void {
  checkedAt.value    = information.checked_at;
  serviceId.value    = information.service_id;
  documentHtml.value = buildDocument(information.html);
  hasInformation     = true;
  state.value        = 'shown';
}

function fail(e: unknown): void {
  errorMessage.value = e instanceof Error ? e.message : 'Die Auskunft konnte nicht geladen werden.';
  state.value        = 'error';
}

function onHidden(): void {
  resolver?.(hasInformation);
  resolver = null;
}

/**
 * Verpackt die Rohantwort in ein eigenständiges Dokument. Der iframe erbt keine
 * Stile der Seite, deshalb hier eine kleine Grundformatierung — der Rest bleibt
 * unverändert, damit man sieht, was die Schnittstelle tatsächlich geliefert hat.
 */
function buildDocument(html: string): string {
  return `<!doctype html><html lang="de"><head><meta charset="utf-8">`
    + `<style>`
    + `body{margin:0;font:14px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;color:#212529;}`
    + `img{max-width:100%;height:auto;}`
    + `</style></head><body>${html}</body></html>`;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

defineExpose({ open });
</script>

<style scoped>
.info-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #ced4da;
  border-top-color: #0d6efd;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.info-empty p {
  margin: 0 0 0.25rem;
}

.info-hint {
  margin-bottom: 0.75rem !important;
  font-size: 0.85rem;
  color: #6c757d;
}

.info-meta {
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  color: #6c757d;
}

.info-frame {
  display: block;
  width: 100%;
  height: min(60vh, 480px);
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  background: #fff;
}

.error {
  color: #dc3545;
}
</style>
